import { useState } from 'react';
import cn from 'classnames';
// Types
import { Interviewer } from 'components/UserScheduleInterview/helpers/localTypes';
import { Organizer } from 'helpers/interview';
// Helpers
import { withCDN } from 'helpers/cdn';
// Ui
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
import Modal from 'ui3/Modal/Modal';
// Components
import SplitLeftRightView from 'components/common3/SplitLeftRightView/SplitLeftRightView';
import CalendarStepForm from './CalendarForm/CalendarForm';
import OrganizerCard from './OrganizerCard/OrganizerCard';
// Styles
import styles from './CalendarStep.module.scss';

type CalendarStepProps = {
  isLoggedIn: boolean;
  interviewId: string | null;
  hostId: string | null;
  loggedUserId: string | null;
  interviewerName?: string;
  organizer?: Organizer;
  onInterviewerChange?: (interviewer: Interviewer) => void;
};

const CalendarStep = ({
  isLoggedIn,
  interviewId,
  hostId,
  loggedUserId,
  interviewerName,
  organizer,
  onInterviewerChange,
}: CalendarStepProps) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const videoUrl = withCDN('/video-tutorial/Interview-tutorial.mp4');

  const handleOpenVideoModal = () => {
    setIsVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  return (
    <div className={styles.container}>
      <SplitLeftRightView
        left={
          <div className={styles.left}>
            {isLoggedIn ? (
              <>
                <Text
                  variant="h2"
                  className={cn(styles.leftHeading, styles.leftH2)}
                >
                  {hostId && organizer
                    ? `Book your interview with ${organizer.firstName} ${organizer.lastName}`
                    : 'Book your interview'}
                </Text>
                <Text
                  variant="body1Regular16"
                  color="lights-medium"
                  className={styles.leftBody}
                >
                  {hostId && organizer
                    ? `Pick your best day/time for your interview on MILLIONS with ${organizer.firstName} ${organizer.lastName}.`
                    : 'Pick your best day/time for your interview on MILLIONS.'}
                </Text>
              </>
            ) : (
              <>
                <Text
                  variant="h2"
                  className={cn(styles.leftHeading, styles.leftH2)}
                >
                  Book your interview
                </Text>
                <Text
                  variant="body1Regular16"
                  color="lights-medium"
                  className={styles.leftBody}
                >
                  Choose an available time to schedule your interview with{' '}
                  {interviewerName}. Typically takes 30 mins.
                </Text>
              </>
            )}
            {hostId && organizer && <OrganizerCard organizer={organizer} />}
          </div>
        }
        right={
          <CalendarStepForm
            interviewId={interviewId}
            hostId={hostId}
            loggedUserId={loggedUserId}
            onInterviewerChange={onInterviewerChange}
          />
        }
      />

      <div className={styles.secondSection}>
        <SplitLeftRightView
          left={
            <div className={styles.whyInterviewLeft}>
              <Text variant="h2" className={styles.whyInterviewH2}>
                Why interview on MILLIONS?
              </Text>
              <Text
                variant="body1Regular16"
                color="lights-medium"
                className={styles.whyInterviewBody}
              >
                Because real stories build real communities. Start your own
                channel or join one, and book your interview in minutes.
              </Text>
              <Button
                variant="secondary"
                icon="playSolid"
                iconPosition="right"
                className={styles.watchVideoButton}
                onClick={handleOpenVideoModal}
              >
                Watch Video
              </Button>
            </div>
          }
          right={
            <img
              src="/images/interview-banner.gif"
              alt="Interview banner"
              className={styles.interviewBanner}
            />
          }
        />
      </div>

      <Modal
        open={isVideoModalOpen}
        onClose={handleCloseVideoModal}
        size="medium"
        showCloseIcon={true}
        focusTrapped={false}
        className={styles.videoModal}
      >
        <div className={styles.videoContainer}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            src={videoUrl}
            controls
            autoPlay
            playsInline
            className={styles.video}
          >
            <source src={videoUrl} />
            Your browser does not support HTML5 video.
          </video>
        </div>
      </Modal>
    </div>
  );
};

export default CalendarStep;
